import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const TrailSidebar = ({ trailData, onViewOnMap, onContactProvider }) => {
  const {
    length,
    elevation,
    duration,
    difficulty,
    theme,
    startPoint,
    endPoint,
    bestSeason,
    accessibility,
    highlights
  } = trailData;

  const getDifficultyColor = (level) => {
    switch (level?.toLowerCase()) {
      case 'easy': return 'text-success bg-success/10';
      case 'moderate': return 'text-warning bg-warning/10';
      case 'challenging': return 'text-destructive bg-destructive/10';
      default: return 'text-muted-foreground bg-muted';
    }
  };

  const trailStats = [
    {
      icon: 'Route',
      label: 'Trail Length',
      value: length,
      color: 'text-primary'
    },
    {
      icon: 'Mountain',
      label: 'Elevation Gain',
      value: elevation,
      color: 'text-accent'
    },
    {
      icon: 'Clock',
      label: 'Duration',
      value: duration,
      color: 'text-success'
    },
    {
      icon: 'TrendingUp',
      label: 'Difficulty',
      value: difficulty,
      color: getDifficultyColor(difficulty)?.split(' ')?.[0]
    }
  ];

  return (
    <div className="space-y-6">
      {/* Trail Overview Card */}
      <div className="bg-card rounded-lg shadow-card p-6">
        <h3 className="font-heading font-semibold text-lg mb-4 flex items-center gap-2">
          <Icon name="Info" size={20} className="text-primary" />
          Trail Information
        </h3>
        
        <div className="space-y-4">
          {trailStats?.map((stat, index) => (
            <div key={index} className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-muted">
                  <Icon name={stat?.icon} size={16} className={stat?.color} />
                </div>
                <span className="text-sm font-medium text-muted-foreground">
                  {stat?.label}
                </span>
              </div>
              <div className={`px-3 py-1 rounded-full text-sm font-semibold ${
                stat?.label === 'Difficulty' ? getDifficultyColor(stat?.value) : 'text-foreground'
              }`}>
                {stat?.value}
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Route Details Card */}
      <div className="bg-card rounded-lg shadow-card p-6">
        <h3 className="font-heading font-semibold text-lg mb-4 flex items-center gap-2">
          <Icon name="MapPin" size={20} className="text-primary" />
          Route Details
        </h3>
        
        <div className="space-y-4">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Icon name="Play" size={14} className="text-success" />
              <span className="text-sm font-medium text-muted-foreground">Start Point</span>
            </div>
            <p className="text-sm text-foreground pl-6">{startPoint}</p>
          </div>
          
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Icon name="Square" size={14} className="text-destructive" />
              <span className="text-sm font-medium text-muted-foreground">End Point</span>
            </div>
            <p className="text-sm text-foreground pl-6">{endPoint}</p>
          </div>
          
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Icon name="Palette" size={14} className="text-accent" />
              <span className="text-sm font-medium text-muted-foreground">Theme</span>
            </div>
            <p className="text-sm text-foreground pl-6">{theme}</p>
          </div>
        </div>
      </div>
      {/* Additional Information Card */}
      <div className="bg-card rounded-lg shadow-card p-6">
        <h3 className="font-heading font-semibold text-lg mb-4 flex items-center gap-2">
          <Icon name="Calendar" size={20} className="text-primary" />
          Planning Information
        </h3>
        
        <div className="space-y-4">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Icon name="Sun" size={14} className="text-warning" />
              <span className="text-sm font-medium text-muted-foreground">Best Season</span>
            </div>
            <p className="text-sm text-foreground pl-6">{bestSeason}</p>
          </div>
          
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Icon name="Accessibility" size={14} className="text-primary" />
              <span className="text-sm font-medium text-muted-foreground">Accessibility</span>
            </div>
            <p className="text-sm text-foreground pl-6">{accessibility}</p>
          </div>
        </div>
      </div>
      {/* Trail Highlights Card */}
      <div className="bg-card rounded-lg shadow-card p-6">
        <h3 className="font-heading font-semibold text-lg mb-4 flex items-center gap-2">
          <Icon name="Star" size={20} className="text-primary" />
          Trail Highlights
        </h3>
        
        <div className="space-y-3">
          {highlights?.map((highlight, index) => (
            <div key={index} className="flex items-start gap-3">
              <div className="p-1 rounded-full bg-accent/20 mt-0.5">
                <Icon name="Check" size={12} className="text-accent" />
              </div>
              <p className="text-sm text-foreground leading-relaxed">{highlight}</p>
            </div>
          ))}
        </div>
      </div>
      {/* Action Buttons */}
      <div className="space-y-3">
        <Button
          variant="default"
          fullWidth
          iconName="Map"
          iconPosition="left"
          onClick={onViewOnMap}
        >
          View on Interactive Map
        </Button>
        
        <Button
          variant="outline"
          fullWidth
          iconName="Phone"
          iconPosition="left"
          onClick={onContactProvider}
        >
          Contact Local Guide
        </Button>
      </div>
    </div>
  );
};

export default TrailSidebar;